/* eslint-disable no-underscore-dangle */
import { useRequest } from 'ahooks';
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Result,
  Row,
  Spin,
  Steps,
  Typography,
} from 'antd';
import LocaleContext from 'locales';
import moment from 'moment';
import React, { useReducer, useState, useEffect } from 'react';
import { AiFillCheckCircle } from 'react-icons/ai';
import {
  doResetPassword,
  getAdditionalAuthChannel,
  getUserContact,
  twoFactorCodeGenerate,
} from 'services/websocket/auth';

/**
 * ResetPassword의 상태 변경
 * @param {object} state 모든 상태
 * @param {{type: string, payload: object}} action
 * @returns {object} state
 * ! 무조건 덮어쓰기 때문에 payload는 object여야 함
 */
function reducer(state, action) {
  const { type, payload } = action;
  switch (type) {
    case 'set-multiple':
      return { ...state, ...payload };
    default:
      return state;
  }
}

// FC: STEP 0 사용자 확인
function UserCheck({ dispatch }) {
  // hooks
  const { locale } = React.useContext(LocaleContext);

  const [loading, setLoading] = useState(false);
  const [userCheckForm] = Form.useForm();

  // 사용자 ID를 확인한 후 Contact 정보를 받는다.
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const res = await getUserContact({ userId: values.userId });
      if (!res.success) {
        if (res.msg === 'two-factor-auth.error.no-info') {
          userCheckForm.setFields([{ name: 'userId', errors: [locale.resource[res.msg]] }]);
        } else {
          message.error(locale.resource[res.msg]);
        }
        return;
      }
      dispatch({ type: 'set-multiple', payload: { ...res.data, currentStep: 1 } });
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form layout="vertical" onFinish={onFinish} form={userCheckForm}>
      <Form.Item
        label={locale.resource['userLog.column.userId']}
        name="userId"
        required
        rules={[{ required: true, message: locale.resource['txt.enter-id'] }]}
      >
        <Input />
      </Form.Item>
      <Button
        type="primary"
        block
        size="large"
        htmlType="submit"
        style={{ marginTop: 8 }}
        loading={loading}
      >
        {locale.resource['common.button.ok']}
      </Button>
    </Form>
  );
}

// FC: STEP 1 사용자 2FA 인증
function UserAuth({ state, dispatch }) {
  // hooks
  const { locale } = React.useContext(LocaleContext);

  // states
  const [contactInfo, setContactInfo] = useState(''); // 사용자가 선택한 인증 채널에 대한 계정 정보
  const [expired, setExpired] = useState(false); // 인증코드 만료 여부
  const [loading, setLoading] = useState(false); // 인증코드 생성 버튼 로딩 상태
  const [verifyLoading, setVerifyLoading] = useState(false); // 비밀번호 재설정 버튼 로딩 상태

  // refs
  const [requestCodeForm] = Form.useForm();

  const onFinishRequest = async (values) => {
    setLoading(true);
    const res = await twoFactorCodeGenerate({
      action: 'reset-password',
      userId: state.userId,
      sendOption: {
        channel: values.channel,
        locale: state.locale,
      },
    });
    setLoading(false);
    if (!res.result) {
      message.error(locale.resource[res.msg]);
      return;
    }
    dispatch({ type: 'set-multiple', payload: { channel: values.channel, generated: res } });
  };

  const channelMap = {
    kakaotalk: state?.telephoneNumber,
    email: state?.mail,
    exmail: state?.externalMail,
  };

  // 채널이 변경되면 Contact 정보를 다시 설정한다.
  const getContactInfo = (e) => {
    if (!e) {
      return;
    }
    const ch = e.target.value;
    if (!ch) {
      setContactInfo(locale.resource['txt.no-registered-information']);
      return;
    }
    requestCodeForm.setFieldsValue({ channel: ch });
    setContactInfo(channelMap[ch] || locale.resource['txt.no-registered-information']);
  };

  // FC: 인증코드 생성 요청 폼
  const RequestAuthCode = () => (
    <Spin spinning={!state.channelOptions.length}>
      <Form onFinish={onFinishRequest} layout="vertical" form={requestCodeForm}>
        <Form.Item
          label={locale.resource['txt.auth-type']}
          required
          name="channel"
          rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
          extra={
            contactInfo && (
              <Alert
                style={{
                  textAlign: 'center',
                  marginTop: 7,
                  padding: '4px 8px',
                  backgroundColor: '#E8ECEF',
                  borderColor: '#E8ECEF',
                }}
                message={contactInfo}
              />
            )
          }
        >
          <Radio.Group options={state.channelOptions} onChange={getContactInfo} />
        </Form.Item>
        <Button
          loading={loading}
          type="primary"
          block
          size="large"
          htmlType="submit"
          style={{ marginTop: 8 }}
        >
          {locale.resource['txt.send-auth-code']}
        </Button>
      </Form>
    </Spin>
  );

  // 비밀번호 재설정 요청(백엔드에서 인증코드 검증 후 임시 비밀번호를 전송한다.)
  const onFinishInput = async (values) => {
    setVerifyLoading(true); // 비밀번호 재설정 로딩 활성화
    try {
      const res = await doResetPassword({
        userId: state.userId,
        otpNumber: values.otpNumber,
        empCode: state.empCode,
        locale: state.locale,
        channel: state.channel,
      });
      if (!res.success) {
        message.error(locale.resource[res.msg]);
        return;
      }
      dispatch({ type: 'set-multiple', payload: { currentStep: 2 } });
    } catch (error) {
      message.error(error.message || 'Error resetting password');
    } finally {
      setVerifyLoading(false); // 비밀번호 재설정 로딩 비활성화
    }
  };

  // FC: 인증코드 입력 폼
  const InputAuthCode = () => (
    <Form onFinish={onFinishInput}>
      <Typography.Paragraph style={{ whiteSpace: 'pre-line' }}>
        {locale.resource['txt.enter-auth-code']}
      </Typography.Paragraph>

      <Form.Item
        name="otpNumber"
        required
        rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
      >
        <Input
          size="large"
          placeholder={locale.resource['txt.enter-code']}
          autoFocus
          autoComplete="off"
          addonAfter={<RemainedTime generated={state.generated} setExpired={setExpired} />}
        />
      </Form.Item>

      <Button
        type="primary"
        size="large"
        block
        htmlType="submit"
        style={{ marginTop: 8 }}
        loading={verifyLoading}
      >
        {locale.resource['txt.reset-password']}
      </Button>
    </Form>
  );

  // 인증 만료로 인한 재요청 시
  const onRetry = () => {
    setExpired(false);
    dispatch({ type: 'set-multiple', payload: { generated: false } });
  };

  if (expired) {
    return (
      <Alert
        showIcon
        type="error"
        description={
          <Row gutter={[0, 8]}>
            <Col span={24}>{locale.resource['txt.auth-expired']}</Col>
            <Col span={24}>
              <Button onClick={onRetry}>{locale.resource['txt.resend']}</Button>
            </Col>
          </Row>
        }
      />
    );
  }

  return state.generated ? <InputAuthCode /> : <RequestAuthCode />;
}

/**
 * FC: 비밀번호 재설정
 * @param {object} resetPwdProps {visible}
 * @param {function} setResetPwdProps resetPwdProps 갱신 함수
 * @returns ReactNode
 */
export default function ResetPassword({ resetPwdProps, setResetPwdProps }) {
  // hooks
  const { locale } = React.useContext(LocaleContext);

  // state 초기값
  const init = {
    currentStep: 0, // 현재 스탭 단계
    generated: false, // 인증 코드 생성에 성공하였는가?
    locale: locale.param, // 인증 코드 발송 시 표시할 언어를 위함
    userId: null,
    empCode: null,
    otpNumber: null,
    channel: null, // 사용자가 선택한 채널
    channelOptions: [], // 인증 채널 목록
  };

  // states
  const [state, dispatch] = useReducer(reducer, init);

  // 컴포넌트 초기화
  const onClose = () => {
    dispatch({ type: 'set-multiple', payload: init });
    setResetPwdProps({ visible: false });
  };

  // Steps 구성을 위한 데이터
  const stepsData = [
    {
      key: 0,
      title: locale.resource['txt.confirm-user'],
      content: <UserCheck state={state} dispatch={dispatch} />,
    },
    {
      key: 1,
      title: locale.resource['txt.user-authentication'],
      content: <UserAuth state={state} dispatch={dispatch} />,
    },
    {
      key: 2,
      title: locale.resource['txt.completed'],
      content: (
        <Result
          status="success"
          title={`${locale.resource['txt.reset-password']} ${locale.resource['txt.completed']}`}
          icon={<AiFillCheckCircle size={48} className="anticon" />}
          subTitle={locale.resource['txt.login-with-changed-password']}
          style={{ padding: 16 }}
          extra={[
            <Button type="primary" key="close" onClick={onClose}>
              {locale.resource['txt.close']}
            </Button>,
          ]}
        />
      ),
    },
  ];

  // variables
  const { visible, userId: loggedInUserId } = resetPwdProps;
  const currentStepData = stepsData.find((el) => el.key === state.currentStep); // 현재 스탭을 구성하기 위한 데이터

  // 비밀번호 재설정용 2FA 채널 목록 요청
  const { run } = useRequest(getAdditionalAuthChannel, {
    manual: true,
    defaultParams: [{ action: 'reset-password' }],
    onSuccess: (data) => {
      // 무조건 빈 배열이라도 오기 때문에 실패는 없음
      const opts = data.channel.map((el) => ({
        label: locale.resource[`txt.${el}`] || el,
        value: el,
      }));
      dispatch({ type: 'set-multiple', payload: { channelOptions: opts } });
    },
  });
  useEffect(() => {
    if (visible) {
      run({ action: 'reset-password' });
    }
  }, [visible, run]);

  return (
    <Modal
      title={locale.resource['txt.reset-password']}
      width={400}
      visible={visible}
      onCancel={onClose}
      maskClosable={false}
      footer={false}
    >
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Steps size="small" current={currentStepData.key}>
            {stepsData.map((el) => (
              <Steps.Step title={el.title} key={el.key} />
            ))}
          </Steps>
        </Col>
        {
          // ldap 773, 532인 경우 사용자에게 고지
          loggedInUserId && (
            <Col span={24}>
              <Alert
                showIcon
                type="warning"
                description={locale.resource['password.change-by-reason']}
              />
            </Col>
          )
        }
        <Col span={24}>
          <div style={{ minHeight: 160 }}>{currentStepData.content}</div>
        </Col>
      </Row>
    </Modal>
  );
}

/**
 * 발급된 인증코드의 남은 시간을 리턴하는 함수
 * 인증코드 유효 기간(3분) 동안 1초마다 렌더링 되기 때문에 별도 Component로 뺌
 * @param {boolean} generated 인증코드 발급 여부에 따라 타이머를 동작시킨다.
 * @param {boolean} setExpired 인증코드의 유효 시간이 만료되었는지 판별한다.
 * @returns ReactNode
 */
function RemainedTime({ generated, setExpired }) {
  const [result, setResult] = useState(undefined); // 만료시각과 현재시각 비교 값(예: 2:58)

  // 만료시각이 임박했는가?
  const impend = result?.split(':')[0] === '0' && Number(result.split(':')[1]) < 10 && true;

  // generated 되면 인증시간 타이머를 동작시킨다.
  useEffect(() => {
    const timer = setInterval(() => {
      if (generated.result) {
        const expireTime = moment(generated.expireDate); // 서버로 부터 받은 만료 시각
        const now = moment();
        const diff = moment.duration(expireTime.diff(now));

        if (diff._milliseconds > 0) {
          setResult(`${diff._data.minutes}:${diff._data.seconds}`);
        } else {
          setResult('0:0');
          setExpired(true);
          clearInterval(timer);
        }
      }
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [generated]);

  return (
    <Typography.Paragraph
      style={{ marginBottom: 0, width: 40, verticalAlign: 'middle' }}
      type={impend ? 'danger' : null}
    >
      {result}
    </Typography.Paragraph>
  );
}
